/**
 * Owner-only Strategic Operations page (Phase 3).
 *
 * Surfaces the remaining 9 items from the 19-item backlog:
 *   #3  Adoption-maturity scores per tenant
 *   #7  Board pack download
 *   #8  Customer-concentration risk
 *  #13  SLA dashboard
 *  #14  Support inbox queue
 *  #16  NPS pulse summary
 *  #17  Public roadmap CRUD
 *  #18  Competitor watch
 *  #19  Feature-ROI scorecard
 *  + Scheduled-evidence-pack subscriptions (per-recipient switch)
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import {
  TrendingUp, Activity, Users, ShieldAlert, Inbox, Smile, Map, Eye,
  Coins, Loader2, Plus, Trash2, Download, Send, ToggleLeft, ToggleRight,
} from "lucide-react";

const BAND_BG = {
  mature: "bg-emerald-100 text-emerald-800",
  developing: "bg-amber-100 text-amber-800",
  nascent: "bg-orange-100 text-orange-800",
  passive: "bg-rose-100 text-rose-800",
  red: "bg-rose-100 text-rose-800",
  amber: "bg-amber-100 text-amber-800",
  green: "bg-emerald-100 text-emerald-800",
  unscored: "bg-slate-100 text-slate-700",
  promoter: "bg-emerald-100 text-emerald-800",
  passive_nps: "bg-slate-100 text-slate-700",
  detractor: "bg-rose-100 text-rose-800",
  open: "bg-slate-100 text-slate-700",
  breached: "bg-rose-100 text-rose-800",
  responded: "bg-violet-100 text-violet-800",
  resolved: "bg-emerald-100 text-emerald-800",
};

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section className="rounded-2xl border border-black/5 bg-white p-6" data-testid={testid}>
    <header className="mb-4 flex items-start gap-3">
      <div className="rounded-xl bg-ek-purple/10 p-2 text-ek-purple"><Icon className="h-5 w-5" /></div>
      <div className="flex-1">
        <h2 className="font-display text-lg font-semibold text-ek-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-ek-ink/60">{subtitle}</p>}
      </div>
    </header>
    {children}
  </section>
);

const fetcher = (url) => api.get(url).then((r) => r.data).catch(() => null);

// ---------- #3 Adoption maturity ----------
function AdoptionMaturitySection() {
  const [data, setData] = useState(null);
  useEffect(() => { fetcher("/owner/adoption-maturity").then(setData); }, []);
  return (
    <Section icon={TrendingUp} title="Adoption maturity (#3)" subtitle="4-pillar score (review cadence, playbook actioning, attestation hygiene, closure discipline) — 0–100 per tenant." testid="adoption-maturity-section">
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <table className="min-w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-ek-ink/50 text-left">
            <tr><th className="py-1">Tenant</th><th className="py-1 text-right">Score</th><th className="py-1">Band</th><th className="py-1">Pillars (cad/act/hyg/cls)</th></tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.tenant_id} className="border-t border-black/5" data-testid={`maturity-row-${r.tenant_id}`}>
                <td className="py-1.5">{r.tenant_name}</td>
                <td className="py-1.5 text-right font-mono font-semibold">{r.score}</td>
                <td className="py-1.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BAND_BG[r.band]}`}>{r.band}</span></td>
                <td className="py-1.5 font-mono text-xs text-ek-ink/60">
                  {r.pillars.review_cadence}/{r.pillars.playbook_actioning}/{r.pillars.attestation_hygiene}/{r.pillars.closure_discipline}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// ---------- #8 Concentration ----------
function ConcentrationSection() {
  const [data, setData] = useState(null);
  useEffect(() => { fetcher("/owner/concentration?threshold_pct=30").then(setData); }, []);
  return (
    <Section icon={Users} title="Customer-concentration risk (#8)" subtitle="HHI + per-tenant share. Flag fires when any tenant exceeds 30% of total MRR." testid="concentration-section">
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label="Total MRR (USD)" value={`$${data.total_mrr.toLocaleString()}`} />
            <Metric label="HHI" value={`${data.hhi} (${data.hhi_band})`} />
            <Metric label="Flagged" value={data.flagged_count} />
          </div>
          {data.by_tenant.length > 0 ? (
            <ul className="mt-3 space-y-1 text-sm" data-testid="concentration-list">
              {data.by_tenant.map((t) => (
                <li key={t.tenant_id} className="flex justify-between border-b border-black/5 py-1">
                  <span>{t.tenant_name}</span>
                  <span className={`font-mono ${t.flag ? "text-rose-700 font-bold" : "text-ek-ink/70"}`}>
                    {t.share_pct}%
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-xs text-ek-ink/50">No paid subscriptions yet — concentration is a forward-looking metric.</p>
          )}
        </>
      )}
    </Section>
  );
}

// ---------- #13 SLA ----------
function SlaSection() {
  const [data, setData] = useState(null);
  useEffect(() => { fetcher("/owner/sla?days=30").then(setData); }, []);
  return (
    <Section icon={Activity} title="SLA dashboard (#13)" subtitle="Uptime % from incident windows + median first-response & resolution from support tickets." testid="sla-section">
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Metric label="Uptime %" value={`${data.uptime_pct}%`} />
          <Metric label="Incidents (30d)" value={data.incident_count} />
          <Metric label="Median first response" value={data.median_first_response_hours != null ? `${data.median_first_response_hours.toFixed(1)}h` : "—"} />
          <Metric label="Median resolution" value={data.median_resolution_hours != null ? `${data.median_resolution_hours.toFixed(1)}h` : "—"} />
        </div>
      )}
    </Section>
  );
}

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-black/5">
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/50">{label}</p>
    <p className="mt-0.5 text-lg font-semibold text-ek-ink">{value}</p>
  </div>
);

// ---------- #14 Support queue ----------
function SupportQueueSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ subject: "", requester_email: "", body: "" });
  const [adding, setAdding] = useState(false);
  const load = () => fetcher("/owner/support-tickets").then(setData);
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/owner/support-tickets", form);
      setForm({ subject: "", requester_email: "", body: "" });
      setAdding(false); load();
      toast.success("Ticket added");
    } catch (err) { toast.error("Add failed"); }
  };
  const mark = async (id, op) => {
    const body = op === "respond" ? { first_response_at: true } : { resolved_at: true };
    try { await api.patch(`/owner/support-tickets/${id}`, body); load(); toast.success("Updated"); }
    catch (err) { toast.error("Update failed"); }
  };
  return (
    <Section icon={Inbox} title="Support inbox queue (#14)" subtitle="SLA-tracked tickets. State auto-flips to 'breached' after 8h with no response." testid="support-queue-section">
      <div className="mb-3 flex justify-end">
        <button onClick={() => setAdding(!adding)} className="rounded-lg border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5" data-testid="ticket-add-toggle">
          <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />{adding ? "Cancel" : "Add ticket"}
        </button>
      </div>
      {adding && (
        <form onSubmit={create} className="mb-4 grid gap-2 rounded-lg border border-black/10 p-3 text-sm" data-testid="ticket-form">
          <input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <input required type="email" placeholder="Requester email" value={form.requester_email} onChange={(e) => setForm({...form, requester_email: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <textarea placeholder="Body (optional)" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} className="rounded border border-black/10 px-2 py-1" rows="2" />
          <button type="submit" className="self-start rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white">Add</button>
        </form>
      )}
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && data.items.length === 0 && <p className="text-xs text-ek-ink/50">No tickets yet.</p>}
      {data && data.items.length > 0 && (
        <ul className="space-y-2" data-testid="ticket-list">
          {data.items.map((t) => (
            <li key={t.ticket_id} className="rounded-lg border border-black/5 p-3" data-testid={`ticket-${t.ticket_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="font-medium">{t.subject}</span>
                  <span className="ml-2 text-xs text-ek-ink/60">{t.requester_email}</span>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BAND_BG[t.sla_state]}`}>{t.sla_state}</span>
              </div>
              <div className="mt-2 flex gap-2">
                {!t.first_response_at && <button onClick={() => mark(t.ticket_id, "respond")} className="rounded bg-violet-600 px-2 py-0.5 text-[11px] text-white">Mark responded</button>}
                {!t.resolved_at && <button onClick={() => mark(t.ticket_id, "resolve")} className="rounded bg-emerald-600 px-2 py-0.5 text-[11px] text-white">Mark resolved</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------- #16 NPS ----------
function NpsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { fetcher("/owner/nps?days=90").then(setData); }, []);
  return (
    <Section icon={Smile} title="NPS pulse (#16)" subtitle="Public POST endpoint /api/public/nps-pulse — embed in governance-pack delivery emails." testid="nps-section">
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Metric label="NPS score" value={data.summary.nps ?? "—"} />
          <Metric label="Responses (90d)" value={data.summary.count} />
          <Metric label="Promoters" value={data.summary.promoters} />
          <Metric label="Detractors" value={data.summary.detractors} />
        </div>
      )}
    </Section>
  );
}

// ---------- #17 Roadmap CRUD ----------
function RoadmapCrudSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ id: "", title: "", category: "planned", quarter: "", blurb: "" });
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/public/roadmap").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/owner/roadmap", form); load(); setAdding(false); setForm({ id: "", title: "", category: "planned", quarter: "", blurb: "" }); toast.success("Added"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed"); }
  };
  const remove = async (id) => {
    if (!confirm(`Remove roadmap item ${id}?`)) return;
    try { await api.delete(`/owner/roadmap/${id}`); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  return (
    <Section icon={Map} title="Public roadmap (#17)" subtitle={(<>Edit what customers see at <a href="/roadmap" className="text-ek-purple underline">/roadmap</a>. Auto-seeded with a safe default.</>)} testid="roadmap-crud-section">
      <div className="mb-3 flex justify-end">
        <button onClick={() => setAdding(!adding)} className="rounded-lg border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5">
          <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />{adding ? "Cancel" : "Add item"}
        </button>
      </div>
      {adding && (
        <form onSubmit={add} className="mb-4 grid gap-2 rounded-lg border border-black/10 p-3 text-sm">
          <input required placeholder="id (slug, e.g. q1-27-rep)" value={form.id} onChange={(e) => setForm({...form, id: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="rounded border border-black/10 px-2 py-1">
            <option value="shipped">shipped</option><option value="in_progress">in_progress</option><option value="planned">planned</option><option value="exploring">exploring</option>
          </select>
          <input required placeholder="Quarter (e.g. Q1 2027)" value={form.quarter} onChange={(e) => setForm({...form, quarter: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <textarea required placeholder="Blurb" value={form.blurb} onChange={(e) => setForm({...form, blurb: e.target.value})} className="rounded border border-black/10 px-2 py-1" rows="2" />
          <button type="submit" className="self-start rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white">Add</button>
        </form>
      )}
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <ul className="space-y-1 text-sm" data-testid="roadmap-crud-list">
          {data.items.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-2 border-b border-black/5 py-1.5">
              <div className="min-w-0 flex-1">
                <span className="font-medium">{r.title}</span>
                <span className="ml-2 text-xs text-ek-ink/50">{r.category} · {r.quarter}</span>
              </div>
              <button onClick={() => remove(r.id)} className="text-xs text-rose-600 hover:underline"><Trash2 className="-mt-0.5 mr-0.5 inline h-3 w-3" />Remove</button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------- #18 Competitor watch ----------
function CompetitorSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ slug: "", name: "", note: "", type: "move", source_url: "" });
  const [adding, setAdding] = useState(false);
  const load = () => fetcher("/owner/competitors").then(setData);
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/owner/competitors", form); load(); setAdding(false); setForm({ slug: "", name: "", note: "", type: "move", source_url: "" }); toast.success("Logged"); }
    catch (err) { toast.error("Add failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Remove this competitor entry?")) return;
    try { await api.delete(`/owner/competitors/${id}`); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  return (
    <Section icon={Eye} title="Competitor watch (#18)" subtitle="Owner-only feed of competitor moves: pricing, features, wins, losses, partnerships." testid="competitor-section">
      <div className="mb-3 flex justify-end">
        <button onClick={() => setAdding(!adding)} className="rounded-lg border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5">
          <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />{adding ? "Cancel" : "Log move"}
        </button>
      </div>
      {adding && (
        <form onSubmit={add} className="mb-4 grid gap-2 rounded-lg border border-black/10 p-3 text-sm">
          <input required placeholder="slug" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <input required placeholder="Competitor name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="rounded border border-black/10 px-2 py-1">
            <option value="move">move</option><option value="pricing">pricing</option><option value="feature">feature</option><option value="win">win</option><option value="loss">loss</option><option value="partnership">partnership</option>
          </select>
          <textarea required placeholder="What happened?" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} className="rounded border border-black/10 px-2 py-1" rows="2" />
          <input placeholder="Source URL (optional)" value={form.source_url} onChange={(e) => setForm({...form, source_url: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <button type="submit" className="self-start rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white">Log</button>
        </form>
      )}
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && data.items.length === 0 && <p className="text-xs text-ek-ink/50">No competitor entries yet.</p>}
      {data && data.items.length > 0 && (
        <ul className="space-y-2" data-testid="competitor-list">
          {data.items.map((c) => (
            <li key={c.competitor_id} className="rounded-lg border border-black/5 p-3" data-testid={`competitor-${c.competitor_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">{c.type}</span>
                </div>
                <button onClick={() => remove(c.competitor_id)} className="text-xs text-rose-600 hover:underline">Remove</button>
              </div>
              <p className="mt-1 text-xs text-ek-ink/70">{c.note}</p>
              {c.source_url && <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-ek-purple underline">source ↗</a>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------- #19 Feature ROI ----------
function FeatureRoiSection() {
  const [data, setData] = useState(null);
  useEffect(() => { fetcher("/owner/feature-roi?days=30").then(setData); }, []);
  return (
    <Section icon={Coins} title="Feature ROI (#19)" subtitle="Per-feature usage × monthly maintenance cost. 'Review' verdict = candidate for sunsetting." testid="feature-roi-section">
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <>
          <p className="mb-2 text-xs text-ek-ink/60">
            Total monthly cost: <span className="font-mono font-semibold">£{data.total_monthly_cost_gbp.toLocaleString()}</span> ·
            Events (30d): <span className="font-mono">{data.total_usage_count.toLocaleString()}</span>
          </p>
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-ek-ink/50 text-left">
              <tr><th className="py-1">Feature</th><th className="py-1 text-right">Events</th><th className="py-1 text-right">Users</th><th className="py-1 text-right">£/month</th><th className="py-1">Verdict</th></tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.prefix} className="border-t border-black/5" data-testid={`feature-roi-${r.prefix}`}>
                  <td className="py-1.5">{r.label}</td>
                  <td className="py-1.5 text-right font-mono">{r.usage_count}</td>
                  <td className="py-1.5 text-right">{r.unique_users}</td>
                  <td className="py-1.5 text-right font-mono">£{r.monthly_cost_gbp}</td>
                  <td className="py-1.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.verdict === "keep" ? "bg-emerald-100 text-emerald-800" : r.verdict === "monitor" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{r.verdict}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Section>
  );
}

// ---------- #7 Board pack ----------
function BoardPackSection() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/owner/board-pack");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `erio-board-pack-${r.data.quarter.replace(" ", "-")}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Board pack downloaded (${r.data.quarter}).`);
    } catch (err) { toast.error("Board pack failed"); } finally { setBusy(false); }
  };
  return (
    <Section icon={ShieldAlert} title="Board pack — quarterly (#7)" subtitle="One-click JSON composite: ERIO's own risks (cash runway, concentration, regulatory, availability) + pipeline + revenue + SLA + tenant health." testid="board-pack-section">
      <button onClick={download} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple-dark disabled:opacity-50" data-testid="board-pack-download">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        Download board pack
      </button>
    </Section>
  );
}

// ---------- Evidence-pack subscriptions ----------
function EvidencePackSubsSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ recipient_email: "", cadence: "monthly", days_window: 30 });
  const [adding, setAdding] = useState(false);
  const load = () => fetcher("/owner/evidence-pack/subscriptions").then(setData);
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/owner/evidence-pack/subscriptions", { ...form, enabled: true }); load(); setAdding(false); setForm({ recipient_email: "", cadence: "monthly", days_window: 30 }); toast.success("Subscribed"); }
    catch (err) { toast.error("Add failed"); }
  };
  const toggle = async (email) => {
    try { await api.patch(`/owner/evidence-pack/subscriptions/${email}/toggle`); load(); }
    catch (err) { toast.error("Toggle failed"); }
  };
  const remove = async (email) => {
    if (!confirm(`Unsubscribe ${email}?`)) return;
    try { await api.delete(`/owner/evidence-pack/subscriptions/${email}`); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  const dispatch = async (dryRun) => {
    try {
      const r = await api.post(`/owner/evidence-pack/dispatch?dry_run=${dryRun}`);
      toast.success(`${dryRun ? "Dry run" : "Dispatched"}: ${r.data.sent.length} sent, ${r.data.skipped.length} skipped`);
    } catch (err) { toast.error("Dispatch failed"); }
  };
  return (
    <Section icon={Send} title="Scheduled evidence-pack subscriptions (switchable)" subtitle="Per-recipient toggle. Monthly or quarterly cadence. Skipped if not due. Per-client subscribable so not everyone gets one — only auditors / GRC reviewers / customer compliance teams who opt in." testid="evidence-pack-subs-section">
      <div className="mb-3 flex flex-wrap gap-2">
        <button onClick={() => setAdding(!adding)} className="rounded-lg border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5">
          <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />{adding ? "Cancel" : "Subscribe"}
        </button>
        <button onClick={() => dispatch(true)} className="rounded-lg border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50">
          Dry-run dispatch
        </button>
        <button onClick={() => dispatch(false)} className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">
          Send now to all due
        </button>
      </div>
      {adding && (
        <form onSubmit={add} className="mb-4 grid gap-2 rounded-lg border border-black/10 p-3 text-sm" data-testid="subscription-form">
          <input required type="email" placeholder="recipient@auditor.com" value={form.recipient_email} onChange={(e) => setForm({...form, recipient_email: e.target.value})} className="rounded border border-black/10 px-2 py-1" />
          <select value={form.cadence} onChange={(e) => setForm({...form, cadence: e.target.value})} className="rounded border border-black/10 px-2 py-1">
            <option value="monthly">monthly</option><option value="quarterly">quarterly</option>
          </select>
          <input type="number" min="1" max="365" placeholder="Period window (days)" value={form.days_window} onChange={(e) => setForm({...form, days_window: parseInt(e.target.value)||30})} className="rounded border border-black/10 px-2 py-1" />
          <button type="submit" className="self-start rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white">Subscribe</button>
        </form>
      )}
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && data.items.length === 0 && <p className="text-xs text-ek-ink/50">No subscriptions yet. Auditor relationships are typically per-engagement.</p>}
      {data && data.items.length > 0 && (
        <ul className="space-y-2" data-testid="subscription-list">
          {data.items.map((s) => (
            <li key={s.recipient_email} className="flex items-center justify-between rounded-lg border border-black/5 p-3" data-testid={`subscription-${s.recipient_email}`}>
              <div>
                <span className="font-medium">{s.recipient_email}</span>
                <span className="ml-2 text-xs text-ek-ink/60">{s.cadence} · {s.days_window}d window</span>
                {s.last_sent_at && <span className="ml-2 text-[11px] text-ek-ink/40">last sent {new Date(s.last_sent_at).toLocaleDateString()}</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(s.recipient_email)} title={s.enabled ? "Disable" : "Enable"} className="text-ek-purple" data-testid={`subscription-toggle-${s.recipient_email}`}>
                  {s.enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5 text-slate-400" />}
                </button>
                <button onClick={() => remove(s.recipient_email)} className="text-xs text-rose-600"><Trash2 className="-mt-0.5 mr-0.5 inline h-3 w-3" />Unsub</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export default function StrategicOps() {
  return (
    <>
      <Seo title="Strategic Ops · ERIO" />
      <Navbar />
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8" data-testid="strategic-ops-page">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-ek-purple font-semibold">EKSELENS · ERIO · OWNER</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ek-ink">Strategic Operations</h1>
          <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
            Phase 3 — adoption maturity, board pack, concentration risk,
            SLA, support queue, NPS, public roadmap CRUD, competitor watch,
            feature ROI, and switchable evidence-pack subscriptions.
          </p>
        </header>
        <AdoptionMaturitySection />
        <BoardPackSection />
        <ConcentrationSection />
        <SlaSection />
        <SupportQueueSection />
        <NpsSection />
        <RoadmapCrudSection />
        <CompetitorSection />
        <FeatureRoiSection />
        <EvidencePackSubsSection />
      </main>
    </>
  );
}
